import { NavLink } from 'react-router-dom';
import { paths } from '@/app/routes/routePaths';
import { useUiStore } from '@/app/store/uiStore';
import { useSettingsStore } from '@/app/store/settingsStore';
import { PermissionGate } from '@/components/common/PermissionGate';
import { PERMISSIONS } from '@/config/constants';
import { clsx } from 'clsx';
import { LayoutDashboard, FileText, CheckSquare, Settings, Users, Menu, Folder as FolderIcon, ChevronRight, ChevronDown, Shield, GitMerge, Building2, Settings2, Trash2, Scan } from 'lucide-react';
import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/services/api/axiosClient';
import { useAuthStore } from '@/app/store/authStore';
import { getStorageUrl } from '@/config/env';

import { selectPermissions } from '@/app/store/authStore';

const navItems = [
  { to: paths.dashboard, label: 'Dashboard', icon: LayoutDashboard, end: true, module: 'dashboard', permission: 'dashboard.view' },
  { to: paths.documents, label: 'Documents', icon: FileText, module: 'documents', permission: 'documents.view' },
  { to: paths.digitization, label: 'Digitization', icon: Scan, end: true, module: 'digitization', permission: 'digitization.view' },
  { to: paths.qualityControl, label: 'Quality Control', icon: CheckSquare, module: 'quality_control', permission: 'quality_control.view' },
  { to: paths.approvals, label: 'Approvals', icon: CheckSquare, module: 'approvals', permission: 'approvals.view' },
];

function FolderTreeItem({ folder, depth = 0 }: { folder: any, depth?: number }) {
  const [expanded, setExpanded] = useState(false);
  const hasChildren = folder.child_folders_count > 0;

  return (
    <div className="w-full">
      <div
        className="flex items-center w-full py-1.5 px-2 hover:bg-slate-800/50 rounded-md cursor-pointer text-slate-300 hover:text-white transition-colors"
        style={{ paddingLeft: `${depth * 12 + 8}px` }}
        onClick={() => setExpanded(!expanded)}
      >
        {hasChildren ? (
          expanded ? <ChevronDown className="h-4 w-4 mr-1 text-slate-500" /> : <ChevronRight className="h-4 w-4 mr-1 text-slate-500" />
        ) : (
          <span className="w-5"></span>
        )}
        <FolderIcon className="h-4 w-4 mr-2 text-indigo-400" />
        <span className="text-sm font-medium truncate">{folder.name}</span>
      </div>
      {expanded && hasChildren && (
        <div className="flex flex-col">
          {/* We would fetch child folders here, but for now we just show a stub or if they are loaded */}
          {folder.child_folders?.map((child: any) => (
            <FolderTreeItem key={child.folder_id} folder={child} depth={depth + 1} />
          ))}
          {!folder.child_folders && (
            <span className="text-xs text-slate-500 italic pl-10 py-1">Loading...</span>
          )}
        </div>
      )}
    </div>
  );
}

export function Sidebar() {
  const isCollapsed = useUiStore((s) => s.isSidebarCollapsed);
  const toggleSidebar = useUiStore((s) => s.toggleSidebar);
  const settings = useSettingsStore((s) => s.settings);

  // Fetch root folders for the tree view
  const { data: rootFolders } = useQuery({
    queryKey: ['folders', 'root'],
    queryFn: async () => {
      const { data } = await apiClient.get('/folders');
      return data;
    }
  });

  const isPlatformAdmin = useAuthStore((s) => s.isPlatformAdmin);
  const currentOrganization = useAuthStore((s) => s.currentOrganization);
  const hideTenantFeatures = isPlatformAdmin && !currentOrganization;
  
  const user = useAuthStore((s) => s.user);
  
  const { hasSuperAccess, userPermissions } = useMemo(() => {
    const roles = user?.roles || [];
    return {
      hasSuperAccess: roles.some((r: any) => ['super-admin', 'developer', 'Admin'].includes(r.name)),
      userPermissions: roles.flatMap((r: any) => r.permissions?.map((p: any) => p.name || p) || [])
    };
  }, [user]);

  const hasPermission = (perm: string) => {
    if (hasSuperAccess) return true;
    return userPermissions.includes(perm);
  };

  return (
    <aside
      className={clsx(
        "flex flex-col bg-slate-950 text-slate-300 transition-all duration-300 ease-in-out border-r border-slate-900 shadow-xl z-20",
        isCollapsed ? "w-20" : "w-64"
      )}
    >
      <div className="flex h-16 items-center justify-between px-4 border-b border-slate-800/60 bg-slate-950">
        {!isCollapsed && (
          <div className="flex items-center space-x-3 overflow-hidden">
            {currentOrganization?.favicon ? (
              <img
                src={getStorageUrl(currentOrganization.favicon)}
                alt="Org Favicon"
                className="h-8 w-8 object-contain shrink-0 rounded-md"
              />
            ) : settings.app_favicon ? (
              <img
                src={getStorageUrl(settings.app_favicon)}
                alt="App Icon"
                className="h-8 w-8 object-contain shrink-0"
              />
            ) : (
              <div className="h-8 w-8 rounded-lg bg-indigo-600 flex items-center justify-center shrink-0 shadow-lg shadow-indigo-600/20">
                <span className="text-white font-bold text-lg">D</span>
              </div>
            )}
            <span className="font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 via-purple-400 to-pink-400 text-xl tracking-tight truncate drop-shadow-sm">
              {settings.app_name || 'DMS'}
            </span>
          </div>
        )}
        <button
          onClick={toggleSidebar}
          className="p-2 rounded-md hover:bg-slate-800 text-slate-400 hover:text-white transition-colors"
        >
          <Menu className="h-5 w-5" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto overflow-x-hidden scrollbar-none py-6 flex flex-col gap-6">
        <nav className="flex flex-col gap-1 px-3">
          <div className="px-3 mb-2 text-xs font-semibold text-slate-500 uppercase tracking-wider">
            {!isCollapsed && "Main Menu"}
          </div>
          {navItems.map((item) => {
            if (hideTenantFeatures && item.to !== paths.dashboard) return null;
            if (item.module && !isPlatformAdmin && !currentOrganization?.enabled_modules?.includes(item.module) && item.module !== 'documents') return null; // Default allow documents for now unless it's in the list
            if (item.module === 'documents' && currentOrganization?.enabled_modules && !currentOrganization.enabled_modules.includes('documents')) return null; // Strict check if documents is a module
            if (item.permission && !hasPermission(item.permission)) return null;
            
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) => clsx(
                  "flex items-center rounded-lg px-3 py-2.5 transition-all duration-200 group relative",
                  isActive
                    ? "bg-indigo-600 text-white shadow-md shadow-indigo-900/20 font-medium"
                    : "text-slate-400 hover:bg-slate-800/80 hover:text-slate-100"
                )}
                title={isCollapsed ? item.label : undefined}
              >
                <Icon className={clsx("h-5 w-5 shrink-0 transition-transform group-hover:scale-110", isCollapsed ? "mx-auto" : "mr-3")} />
                {!isCollapsed && <span>{item.label}</span>}
              </NavLink>
            );
          })}

          {!hideTenantFeatures && (
            <>
              {(!currentOrganization || currentOrganization?.enabled_modules?.includes('users')) && hasPermission('users.view') && (
              <NavLink
                to={paths.adminUsers}
                className={({ isActive }) => clsx(
                  "flex items-center rounded-lg px-3 py-2.5 transition-all duration-200 group mt-1",
                  isActive
                    ? "bg-indigo-600 text-white shadow-md shadow-indigo-900/20 font-medium"
                    : "text-slate-400 hover:bg-slate-800/80 hover:text-slate-100"
                )}
                title={isCollapsed ? "Users" : undefined}
              >
                <Users className={clsx("h-5 w-5 shrink-0 transition-transform group-hover:scale-110", isCollapsed ? "mx-auto" : "mr-3")} />
                {!isCollapsed && <span>Users</span>}
              </NavLink>
              )}
              {(!currentOrganization || currentOrganization?.enabled_modules?.includes('roles')) && hasPermission('roles.view') && (
              <NavLink
                to={paths.adminRoles}
                className={({ isActive }) => clsx(
                  "flex items-center rounded-lg px-3 py-2.5 transition-all duration-200 group mt-1",
                  isActive
                    ? "bg-indigo-600 text-white shadow-md shadow-indigo-900/20 font-medium"
                    : "text-slate-400 hover:bg-slate-800/80 hover:text-slate-100"
                )}
                title={isCollapsed ? "Roles" : undefined}
              >
                <Shield className={clsx("h-5 w-5 shrink-0 transition-transform group-hover:scale-110", isCollapsed ? "mx-auto" : "mr-3")} />
                {!isCollapsed && <span>Roles</span>}
              </NavLink>
              )}
              {(!currentOrganization || currentOrganization?.enabled_modules?.includes('departments')) && hasPermission('departments.view') && (
              <NavLink
                to={paths.adminDepartments}
                className={({ isActive }) => clsx(
                  "flex items-center rounded-lg px-3 py-2.5 transition-all duration-200 group mt-1",
                  isActive
                    ? "bg-indigo-600 text-white shadow-md shadow-indigo-900/20 font-medium"
                    : "text-slate-400 hover:bg-slate-800/80 hover:text-slate-100"
                )}
                title={isCollapsed ? "Departments" : undefined}
              >
                <Building2 className={clsx("h-5 w-5 shrink-0 transition-transform group-hover:scale-110", isCollapsed ? "mx-auto" : "mr-3")} />
                {!isCollapsed && <span>Departments</span>}
              </NavLink>
              )}
              
              {(!currentOrganization || currentOrganization?.enabled_modules?.includes('workflows')) && hasPermission('workflows.view') && (
              <NavLink
                to={paths.adminWorkflows}
                className={({ isActive }) => clsx(
                  "flex items-center rounded-lg px-3 py-2.5 transition-all duration-200 group mt-1",
                  isActive
                    ? "bg-indigo-600 text-white shadow-md shadow-indigo-900/20 font-medium"
                    : "text-slate-400 hover:bg-slate-800/80 hover:text-slate-100"
                )}
                title={isCollapsed ? "Workflows" : undefined}
              >
                <GitMerge className={clsx("h-5 w-5 shrink-0 transition-transform group-hover:scale-110", isCollapsed ? "mx-auto" : "mr-3")} />
                {!isCollapsed && <span>Workflows</span>}
              </NavLink>
              )}
              {(!currentOrganization || currentOrganization?.enabled_modules?.includes('audit_logs')) && hasPermission('audit_logs.view') && (
              <NavLink
                to={paths.adminAudit}
                className={({ isActive }) => clsx(
                  "flex items-center rounded-lg px-3 py-2.5 transition-all duration-200 group mt-1",
                  isActive
                    ? "bg-indigo-600 text-white shadow-md shadow-indigo-900/20 font-medium"
                    : "text-slate-400 hover:bg-slate-800/80 hover:text-slate-100"
                )}
                title={isCollapsed ? "Audit Logs" : undefined}
              >
                <FileText className={clsx("h-5 w-5 shrink-0 transition-transform group-hover:scale-110", isCollapsed ? "mx-auto" : "mr-3")} />
                {!isCollapsed && <span>Audit Logs</span>}
              </NavLink>
              )}
              
              {(!currentOrganization || currentOrganization?.enabled_modules?.includes('trash')) && hasPermission('trash.view') && (
              <NavLink
                to={paths.adminTrash}
                className={({ isActive }) => clsx(
                  "flex items-center rounded-lg px-3 py-2.5 transition-all duration-200 group mt-1",
                  isActive
                    ? "bg-indigo-600 text-white shadow-md shadow-indigo-900/20 font-medium"
                    : "text-slate-400 hover:bg-slate-800/80 hover:text-slate-100"
                )}
                title={isCollapsed ? "Trash" : undefined}
              >
                <Trash2 className={clsx("h-5 w-5 shrink-0 transition-transform group-hover:scale-110", isCollapsed ? "mx-auto" : "mr-3")} />
                {!isCollapsed && <span>Trash</span>}
              </NavLink>
              )}
              
            </>
          )}

          {useAuthStore(s => s.user?.roles.some(r => r.name === 'developer')) && (
            <>
              <NavLink
                to={paths.adminActionControl}
                className={({ isActive }) => clsx(
                  "flex items-center rounded-lg px-3 py-2.5 transition-all duration-200 group mt-1",
                  isActive
                    ? "bg-indigo-600 text-white shadow-md shadow-indigo-900/20 font-medium"
                    : "text-slate-400 hover:bg-slate-800/80 hover:text-slate-100"
                )}
                title={isCollapsed ? "Action Control" : undefined}
              >
                <Settings2 className={clsx("h-5 w-5 shrink-0 transition-transform group-hover:scale-110", isCollapsed ? "mx-auto" : "mr-3")} />
                {!isCollapsed && <span>Action Control</span>}
              </NavLink>

            </>
          )}

          {useAuthStore(s => s.isPlatformAdmin) && !useAuthStore(s => s.currentOrganization) && (
            <>
              <NavLink
                to="/platform/organizations"
                className={({ isActive }) => clsx(
                  "flex items-center rounded-lg px-3 py-2.5 transition-all duration-200 group mt-4",
                  isActive
                    ? "bg-indigo-600 text-white shadow-md shadow-indigo-900/20 font-medium"
                    : "text-slate-400 hover:bg-slate-800/80 hover:text-slate-100"
                )}
                title={isCollapsed ? "Organizations" : undefined}
              >
                <Building2 className={clsx("h-5 w-5 shrink-0 transition-transform group-hover:scale-110", isCollapsed ? "mx-auto" : "mr-3")} />
                {!isCollapsed && <span>Organizations</span>}
              </NavLink>

              <NavLink
                to={paths.settings}
                className={({ isActive }) => clsx(
                  "flex items-center rounded-lg px-3 py-2.5 transition-all duration-200 group mt-1",
                  isActive
                    ? "bg-indigo-600 text-white shadow-md shadow-indigo-900/20 font-medium"
                    : "text-slate-400 hover:bg-slate-800/80 hover:text-slate-100"
                )}
                title={isCollapsed ? "Settings" : undefined}
              >
                <Settings className={clsx("h-5 w-5 shrink-0 transition-transform group-hover:scale-110", isCollapsed ? "mx-auto" : "mr-3")} />
                {!isCollapsed && <span>Settings</span>}
              </NavLink>
            </>
          )}
        </nav>

        {!isCollapsed && !hideTenantFeatures && (
          <div className="px-3 flex flex-col">
            <div className="px-3 mb-2 text-xs font-semibold text-slate-500 uppercase tracking-wider">
              Workspaces
            </div>
            <div className="bg-slate-900/50 rounded-lg p-2 border border-slate-800/50">
              {rootFolders?.length > 0 ? (
                rootFolders.map((folder: any) => (
                  <FolderTreeItem key={folder.folder_id} folder={folder} />
                ))
              ) : (
                <div className="px-2 py-3 text-sm text-slate-500 text-center">
                  No folders available
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </aside>
  );
}
